/**
 * DocumentChecklistScreen - Employment document submission checklist (雇入時提出書類チェック表).
 * Shown after the user agrees to personal info consent from the menu "雇用書類を提出".
 *
 * - Title and intro text (translated)
 * - StepRoadmap: アップロード書類 / 記入書類 / 確認書類
 * - 戻る (Back) and 送信 (Submit) buttons. Submit disabled until all steps complete; then blue.
 *
 * All copy uses t() so the screen respects the user's language (ja/en).
 */
import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import StepRoadmap from '../components/StepRoadmap';
import type { StepProgress } from '../types';
import { interpolateI18n } from '../../fillingForms/chat/utils/interpolateI18n';
import './DocumentChecklistScreen.css';

export interface DocumentChecklistScreenProps {
  /** Called when user clicks 戻る - return to menu */
  onBack: () => void;
  /** Called when user clicks 送信 and all steps are complete */
  onSubmit?: () => void;
  /** When user clicks the「アップロード書類」step in the roadmap, open the chat flow */
  onOpenVariousCopies?: () => void;
  /** When user clicks the「記入書類」step in the roadmap, open filling-forms chat flow */
  onOpenFillingForms?: () => void;
  /** When user clicks the「確認書類」step in the roadmap */
  onOpenConfirmationForms?: () => void;
  /**
   * Total for アップロード書類 step (X in 0/X). When set, should match admin-selected
   * document count from invitation_various_copies; otherwise defaults to 11.
   */
  variousCopiesTotal?: number;
  /**
   * Number of アップロード書類 already completed (choice made or uploaded).
   * When set, shows progress e.g. 3/5; when equal to total, step shows green (completed).
   */
  variousCopiesCompleted?: number;
  /** Optional initial progress; defaults to all zeros */
  initialProgress?: StepProgress;
  /** Optional red indicator badge for アップロード書類 step (e.g. rejected copies needing resubmission). */
  copiesBadgeCount?: number;
  /** Optional red indicator badge for 記入書類 step (e.g. rejected forms needing resubmission). */
  fillInBadgeCount?: number;
  /** Optional red indicator badge for 確認書類 step. */
  confirmationBadgeCount?: number;
  /** Total 記入書類 forms (excluding 基本情報/家族情報); screen adds +2 for those. */
  formsToFillTotal?: number;
  /** Completed 記入書類 forms count (excluding profile sections). */
  formsToFillCompleted?: number;
  /** Total 確認書類 forms from invitation. Omit / undefined while chat-flow is loading. */
  confirmationFormsTotal?: number;
  /** Completed 確認書類 forms count. Omit / undefined while chat-flow is loading. */
  confirmationFormsCompleted?: number;
  /** General information completion status (counts as +1 for 記入書類). */
  generalInfoStatus?: 'notyet' | 'in_progress' | 'final_confirm' | 'completed';
  /** Family information completion status (counts as +1 for 記入書類). */
  familyInfoStatus?: 'notyet' | 'in_progress' | 'final_confirm' | 'completed';
  /** Invitation submission deadline (yyyy/MM/dd); shown under intro when set. */
  submissionDeadline?: string;
}

const DEFAULT_PROGRESS: StepProgress = {
  copies: 0,
  fillIn: 0,
  confirmation: 0,
};

const STEP_TOTALS = {
  copies: 11,
  fillIn: 17,
  /** Placeholder while invitation forms_to_fill are still loading / unavailable. */
  confirmation: 8,
} as const;

const DocumentChecklistScreen: React.FC<DocumentChecklistScreenProps> = ({
  onBack,
  onSubmit,
  onOpenVariousCopies,
  onOpenFillingForms,
  onOpenConfirmationForms,
  variousCopiesTotal,
  variousCopiesCompleted,
  initialProgress = DEFAULT_PROGRESS,
  copiesBadgeCount,
  fillInBadgeCount,
  confirmationBadgeCount,
  formsToFillTotal,
  formsToFillCompleted,
  confirmationFormsTotal,
  confirmationFormsCompleted,
  generalInfoStatus,
  familyInfoStatus,
  submissionDeadline,
}) => {
  const { t } = useLanguage();
  const [progress] = useState<StepProgress>(initialProgress);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  /** Total documents admin asked for; fallback to legacy 11 when not loaded */
  const copiesTotal =
    typeof variousCopiesTotal === 'number' && variousCopiesTotal >= 0
      ? variousCopiesTotal
      : STEP_TOTALS.copies;

  /** Completed count for アップロード書類: from API when set, else from local progress */
  const copiesCompleted =
    typeof variousCopiesCompleted === 'number' && variousCopiesCompleted >= 0
      ? variousCopiesCompleted
      : progress.copies;

  const fillInTotal =
    typeof formsToFillTotal === 'number' && formsToFillTotal >= 0
      ? formsToFillTotal + 2
      : STEP_TOTALS.fillIn;

  const generalDone =
    generalInfoStatus === 'completed' || generalInfoStatus === 'final_confirm';
  const familyDone =
    familyInfoStatus === 'completed' || familyInfoStatus === 'final_confirm';

  const fillInCompleted =
    typeof formsToFillCompleted === 'number' && formsToFillCompleted >= 0
      ? formsToFillCompleted + (generalDone ? 1 : 0) + (familyDone ? 1 : 0)
      : progress.fillIn;

  const confirmationLoaded = typeof confirmationFormsTotal === 'number';
  const confirmationTotal = confirmationLoaded
    ? Math.max(0, confirmationFormsTotal)
    : STEP_TOTALS.confirmation;

  const confirmationCompleted =
    typeof confirmationFormsCompleted === 'number' && confirmationFormsCompleted >= 0
      ? confirmationFormsCompleted
      : progress.confirmation;

  /** Steps with translated labels for the roadmap */
  const steps = useMemo(() => {
    const list = [
      {
        id: 'copies',
        label: t('submitDocuments.stepCopies'),
        completed: copiesCompleted,
        total: copiesTotal,
        badgeCount: copiesBadgeCount,
      },
      {
        id: 'fillIn',
        label: t('submitDocuments.stepFillIn'),
        completed: fillInCompleted,
        total: fillInTotal,
        badgeCount: fillInBadgeCount,
      },
    ];
    // Show while loading (totals undefined) or when the invitation includes confirmation docs.
    if (!confirmationLoaded || confirmationTotal > 0) {
      list.push({
        id: 'confirmation',
        label: t('submitDocuments.stepConfirmation'),
        completed: confirmationCompleted,
        total: confirmationTotal,
        badgeCount: confirmationBadgeCount,
      });
    }
    return list;
  }, [
    t,
    copiesCompleted,
    copiesTotal,
    fillInCompleted,
    fillInTotal,
    confirmationCompleted,
    confirmationTotal,
    confirmationLoaded,
    copiesBadgeCount,
    fillInBadgeCount,
    confirmationBadgeCount,
  ]);

  /** Index of the first step where completed < total (current step) */
  const currentStepIndex = useMemo(() => {
    const idx = steps.findIndex((s) => s.completed < s.total);
    return idx >= 0 ? idx : steps.length;
  }, [steps]);

  /** True when every step has completed === total */
  const allComplete = useMemo(
    () => steps.every((s) => s.completed >= s.total),
    [steps]
  );

  const handleSubmitClick = () => {
    if (!allComplete || !onSubmit) return;
    setShowSubmitConfirm(true);
  };

  const handleConfirmSubmit = () => {
    setShowSubmitConfirm(false);
    if (onSubmit) onSubmit();
  };

  const handleCancelSubmit = () => {
    setShowSubmitConfirm(false);
  };

  return (
    <div className="document-checklist-container">
      <div className="document-checklist-content">
        <h1 className="document-checklist__title">{t('submitDocuments.title')}</h1>

        <p className="document-checklist__intro">{t('submitDocuments.intro')}</p>
        {submissionDeadline?.trim() ? (
          <p className="document-checklist__deadline">
            {interpolateI18n(t('submitDocuments.deadline'), {
              date: submissionDeadline.trim(),
            })}
          </p>
        ) : null}

        <hr className="document-checklist__hr" />

        <div className="document-checklist__roadmap">
          <StepRoadmap
            steps={steps}
            currentIndex={currentStepIndex < steps.length ? currentStepIndex : steps.length - 1}
            onStepClick={
              onOpenVariousCopies || onOpenFillingForms || onOpenConfirmationForms
                ? (index) => {
                    if (steps[index]?.id === 'copies') onOpenVariousCopies?.();
                    if (steps[index]?.id === 'fillIn') onOpenFillingForms?.();
                    if (steps[index]?.id === 'confirmation') onOpenConfirmationForms?.();
                  }
                : undefined
            }
          />
        </div>

        <hr className="document-checklist__hr" />

        <div className="document-checklist__actions">
          <button
            type="button"
            className="document-checklist__btn document-checklist__btn--back"
            onClick={onBack}
          >
            {t('submitDocuments.back')}
          </button>
          <button
            type="button"
            className="document-checklist__btn document-checklist__btn--submit"
            disabled={!onSubmit || !allComplete}
            onClick={handleSubmitClick}
          >
            {t('submitDocuments.submit')}
          </button>
        </div>
      </div>

      {showSubmitConfirm && (
        <div className="document-checklist__confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="submit-confirm-title">
          <div className="document-checklist__confirm-box">
            <p id="submit-confirm-title" className="document-checklist__confirm-message">
              {t('submitDocuments.submitConfirmMessage')}
            </p>
            <div className="document-checklist__confirm-actions">
              <button
                type="button"
                className="document-checklist__btn document-checklist__btn--back"
                onClick={handleCancelSubmit}
              >
                {t('submitDocuments.submitConfirmNo')}
              </button>
              <button
                type="button"
                className="document-checklist__btn document-checklist__btn--submit"
                onClick={handleConfirmSubmit}
              >
                {t('submitDocuments.submitConfirmYes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentChecklistScreen;
